import React, { useEffect, useState } from "react";
import { FaChevronDown, FaRegCalendarAlt } from "react-icons/fa";
import { HiOutlineUserGroup } from "react-icons/hi2";
import { useFavorites } from "./FavoritesContext";
import PostEventSheet from "./PostEventSheet";
import HUB_ORGS from "./hubOrgsData";
import "./HubQuickActions.css";

/* =========================================================
   HubQuickActions   modern Quick Access.

   • Community Connect (purple) toggles the community
     organization links open IN PLACE   same behavior the old
     Important Links button had, restyled.
   • Post an event (gold) -> community picker.

   Desktop:  <HubQuickActions />          right rail
   Mobile:   <HubQuickActions inline />   main flow (<=720px)
   ========================================================= */

/* Single source of truth: the same list HubOrgs (the nonprofits
   section) renders. Edit hubOrgsData.js to change both. */
const COMMUNITY_ORGS = HUB_ORGS.map((org) => ({
  name: org.name,
  url: org.website || org.facebook || org.instagram || "",
})).filter((org) => org.url);

export default function HubQuickActions({ inline = false }) {
  const [open, setOpen] = useState(false);
  const [postOpen, setPostOpen] = useState(false);
  const [pendingPost, setPendingPost] = useState(false);
  const { user, requestSignInPrompt } = useFavorites();

  /* one live instance per viewport: sidebar on desktop, inline on
     mobile   only the visible one reacts */
  const isActiveInstance = () =>
    inline === window.matchMedia("(max-width: 720px)").matches;

  const openPostFlow = () => {
    if (!isActiveInstance()) return;
    if (user) {
      setPostOpen(true);
      return;
    }
    setPendingPost(true);
    requestSignInPrompt({
      kind: "event",
      name: "your community",
      onCancel: () => setPendingPost(false),
    });
  };

  useEffect(() => {
    if (pendingPost && user) {
      setPendingPost(false);
      setPostOpen(true);
    }
  }, [pendingPost, user]);

  /* Hero chip + events-rail CTA open the same form via this event */
  useEffect(() => {
    const handler = () => openPostFlow();
    window.addEventListener("gohub:post-event", handler);
    return () => window.removeEventListener("gohub:post-event", handler);
  });

  return (
    <section
      className={`hqa ${inline ? "hqa--inline" : ""}`}
      aria-label="Quick access"
    >
      {!inline && <span className="hqa-kick">Quick access</span>}

      <div className="hqa-btns">
        <button
          type="button"
          className="hqa-btn hqa-btn--purple"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <span className="hqa-ic" aria-hidden="true">
            <HiOutlineUserGroup />
          </span>
          <span className="hqa-tx">
            <strong>Community Connect</strong>
            <small>
              {open
                ? `${COMMUNITY_ORGS.length} local organizations`
                : "Meet your communities"}
            </small>
          </span>
          <span
            className={`hqa-chev ${open ? "is-open" : ""}`}
            aria-hidden="true"
          >
            <FaChevronDown />
          </span>
        </button>

        <button
          type="button"
          className="hqa-btn hqa-btn--gold"
          onClick={openPostFlow}
        >
          <span className="hqa-ic" aria-hidden="true">
            <FaRegCalendarAlt />
          </span>
          <span className="hqa-tx">
            <strong>Post an event</strong>
            <small>Free · pick your community</small>
          </span>
        </button>
      </div>

      {postOpen && (
        <PostEventSheet
          askCommunity
          initialEmail={user?.email || ""}
          onClose={() => setPostOpen(false)}
        />
      )}

      {open && (
        <ul className="hqa-orgs" aria-label="Community organizations">
          {COMMUNITY_ORGS.map((org) => (
            <li key={org.name}>
              <a
                className="hqa-org"
                href={org.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {org.name}
              </a>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
